/// <reference types="vite/client" />

import type { AxiosRequestConfig } from 'axios'
import axios, { isAxiosError } from 'axios'
import * as R from 'remeda'

import { sessionStore } from '@gto/session'

import { readImpersonation } from '@/api/impersonation-storage'
import { getRuntimeConfig } from '@/config/runtime-env'
import { getOAuthClientId } from '@/utils/oauth'

// `globalThis.localStorage` can be missing or a stub without Storage methods —
// e.g. embedded contexts, sandboxed iframes, browser private-mode quirks, and
// Node 25's `--localstorage-file`-without-path stub surfaced by the test
// harness. Guard every access so the request interceptor cannot throw a
// `TypeError: localStorage.getItem is not a function` and corrupt every API
// call (which then surfaces as a confusing downstream "Cannot destructure
// 'data' of undefined" after a malformed interceptor rejection).
const safeStorageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeStorageSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // no-op — best-effort persistence
  }
}

const safeStorageRemove = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch {
    // no-op — best-effort removal
  }
}

type DoatTokenResponse = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  scope: string
}

/**
 * Thrown by the response interceptor when a 401 could not be recovered — the
 * DOAT refresh failed (real session expiry). The interceptor swallows the
 * original `AxiosError` and `handleAuthFailure` clears the session, but it does
 * NOT route the user anywhere. Callers (e.g. `store.refetchPartner`) match THIS
 * type to distinguish a genuinely expired session from a transient transport
 * failure and drive the `/login` redirect. A bare `Error` would be
 * indistinguishable from any other plain rejection, so the expiry signal has to
 * be a nominal type, not a message string.
 */
export class AuthRefreshFailedError extends Error {
  constructor() {
    super('Token refresh failed')
    this.name = 'AuthRefreshFailedError'
  }
}

export const isAuthRefreshFailedError = (error: unknown): error is AuthRefreshFailedError =>
  error instanceof AuthRefreshFailedError

const baseURL = getRuntimeConfig().apiBaseUrl

export const axiosInstance = axios.create({
  baseURL,
})

let isRefreshing = false
let failedRequests: { resolve: (value: unknown) => void; reject: (reason?: unknown) => void }[] = []

const processQueue = (error?: Error) => {
  failedRequests.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(true)
    }
  })
  failedRequests = []
}

axiosInstance.interceptors.request.use((request) => {
  const accessToken = sessionStore.getState().accessToken
  const lang = safeStorageGet('lang') ?? 'en'

  if (R.isNonNullish(accessToken) && !R.isEmpty(accessToken)) {
    request.headers.Authorization = `Bearer ${accessToken}`
  }

  request.headers['Accept-Language'] = lang

  // Admin impersonation: when an admin selected "Log in as <partner>",
  // /v4/affiliates/me/* resolves to that partner on the backend. The header
  // is the only signal — the BE enforces (verified admin only) and 403s
  // any write attempt while impersonated; the FE just attaches it on every
  // request so the entire portal renders as the target partner.
  //
  // UX trade-off on writes — accepted: the header attaches on every method
  // (GET/POST/PATCH/DELETE). If an admin clicks a write button while
  // impersonating, the BE returns a 403 and `useMutationErrorHandler` renders
  // the generic mutation error alert — NOT a specific "writes are disabled
  // while impersonating" message. Surfacing that distinction would mean
  // either a typed error thrown here (and consumed in the shared handler) or
  // a banner-side guard on every write CTA. We chose the smaller surface:
  // BE is the single enforcement point, the banner is a clear "you are
  // impersonating" signal, and partner-facing write affordances are rare in
  // the surfaces an impersonating admin typically opens (read-heavy:
  // dashboard, commissions, coupons, payouts). Revisit if QA reports
  // confused operators.
  const impersonation = readImpersonation()
  if (impersonation !== null) {
    request.headers['X-Impersonate-Partner-Id'] = impersonation.id
  }

  request.paramsSerializer = {
    indexes: null,
  }

  return request
})

axiosInstance.interceptors.response.use(
  (request) => request,
  async (error: unknown) => {
    if (isAxiosError(error)) {
      const originalRequest = error.config
      if (originalRequest && error.response?.status === 401) {
        if (isRefreshing) {
          try {
            await new Promise((resolve, reject) => {
              failedRequests = [...failedRequests, { resolve, reject }]
            })

            return await axiosInstance(originalRequest)
          } catch (e) {
            return Promise.reject(e instanceof Error ? e : new Error(String(e)))
          }
        }

        try {
          isRefreshing = true
          const refresh = safeStorageGet('user_refresh')

          // BE Ninja wrapper at /v4/accounts/oauth2/token/ decodes the body as JSON,
          // not RFC 6749 §6 form-encoded — mirrors the authorization_code exchange in
          // _auth/login/oauth/-loader-logic.ts. Bypass axiosInstance so the request
          // interceptor does not attach the (expired) Bearer token to the refresh call.
          const response = await axios.post<DoatTokenResponse>(
            `${baseURL}/v4/accounts/oauth2/token/`,
            {
              grant_type: 'refresh_token',
              refresh_token: refresh ?? '',
              client_id: getOAuthClientId(),
            },
          )

          const { access_token, refresh_token } = response.data
          sessionStore.getState().setAccessToken(access_token)
          // DOAT rotates refresh tokens by default — the previous one is now invalid.
          // Persist the rotated value so the next 401 has a usable refresh token.
          safeStorageSet('user_refresh', refresh_token)
          axiosInstance.defaults.headers.common.Authorization = `Bearer ${access_token}`
          isRefreshing = false
          processQueue()
        } catch {
          isRefreshing = false
          processQueue(new AuthRefreshFailedError())
          handleAuthFailure()

          return Promise.reject(new AuthRefreshFailedError())
        }

        try {
          return await axiosInstance(originalRequest)
        } catch (retryError) {
          if (axios.isAxiosError(retryError) && retryError.response?.status === 401) {
            handleAuthFailure()

            return Promise.reject(new AuthRefreshFailedError())
          }

          return retryError instanceof Error
            ? Promise.reject(retryError)
            : Promise.reject(new Error('Original request failed after the token was refreshed'))
        }
      }

      return Promise.reject(error)
    }

    // Non-axios rejection (e.g. a synchronous TypeError thrown by the request
    // interceptor when localStorage is a stub, or anything else that crashes
    // before axios produces an AxiosError). Returning here without rejecting
    // would let axios treat the rejection-handler's `undefined` as a recovery
    // and resolve the request to `undefined`, which then crashes downstream
    // `.then(({ data }) => data)` callers with a misleading TypeError. Always
    // re-reject so the failure reaches the query layer with its real cause.
    return Promise.reject(error instanceof Error ? error : new Error(String(error)))
  },
)

const handleAuthFailure = () => {
  safeStorageRemove('user_refresh')
  sessionStore.getState().clearAccessToken()
}

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> =>
  axiosInstance<T>({ ...config }).then(({ data }) => data)

export default customInstance
